import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Users, FileText, Receipt, Key, MessageSquare, AlertCircle, TrendingUp, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PIE_COLORS = ['#D97706', '#16A34A', '#DC2626'];
const CAT_COLORS = ['#0D47A1', '#D32F2F', '#1A237E', '#B71C1C'];

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState<any>({});
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/analytics`).then(r => setAnalytics(r.data)).catch(() => {});
  }, []);

  const cards = [
    { icon: Users, label: t('admin.total_users'), value: stats.total_users || 0, color: '#0D47A1' },
    { icon: FileText, label: t('admin.total_quotes'), value: stats.total_quotes || 0, color: '#D32F2F' },
    { icon: AlertCircle, label: t('admin.pending_quotes'), value: stats.pending_quotes || 0, color: '#D97706' },
    { icon: Receipt, label: t('admin.total_invoices'), value: stats.total_invoices || 0, color: '#1A237E' },
    { icon: Key, label: t('admin.total_licenses'), value: stats.total_licenses || 0, color: '#B71C1C' },
    { icon: MessageSquare, label: t('admin.total_contacts'), value: stats.total_contacts || 0, color: '#059669' },
  ];

  const statusData = analytics ? [
    { name: 'Pending', value: analytics.quotes_by_status?.pending || 0 },
    { name: 'Approved', value: analytics.quotes_by_status?.approved || 0 },
    { name: 'Rejected', value: analytics.quotes_by_status?.rejected || 0 },
  ] : [];

  const categoryData = analytics ? [
    { name: 'Licenses', value: analytics.quotes_by_category?.licenses || 0 },
    { name: 'Support', value: analytics.quotes_by_category?.support || 0 },
    { name: 'Procurement', value: analytics.quotes_by_category?.procurement || 0 },
    { name: 'Hosting', value: analytics.quotes_by_category?.hosting || 0 },
  ] : [];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="dashboard" />
      <main data-testid="admin-dashboard" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32, overflowY: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700, marginBottom: 24 }}>{t('admin.dashboard')}</h1>

          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
            {cards.map(c => {
              const Icon = c.icon;
              return (
                <div key={c.label} data-testid="admin-stat-card" style={{ background: 'white', border: '1px solid var(--border)', padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${c.color}10` }}>
                      <Icon size={18} style={{ color: c.color }} strokeWidth={1.5} />
                    </div>
                  </div>
                  <p style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>{c.value}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}>{c.label}</p>
                </div>
              );
            })}
          </div>

          {/* Analytics highlight cards */}
          {analytics && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
              <div style={{ background: 'linear-gradient(135deg, #0D47A1, #1A237E)', padding: 24, color: 'white' }}>
                <DollarSign size={24} style={{ opacity: 0.7, marginBottom: 8 }} />
                <p style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>${(analytics.total_revenue || 0).toLocaleString()}</p>
                <p style={{ fontSize: 12, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total Revenue</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #16A34A, #059669)', padding: 24, color: 'white' }}>
                <Key size={24} style={{ opacity: 0.7, marginBottom: 8 }} />
                <p style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>{analytics.active_licenses || 0}</p>
                <p style={{ fontSize: 12, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Active Licenses</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #D32F2F, #B71C1C)', padding: 24, color: 'white' }}>
                <TrendingUp size={24} style={{ opacity: 0.7, marginBottom: 8 }} />
                <p style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>{analytics.new_users_30d || 0}</p>
                <p style={{ fontSize: 12, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em' }}>New Users (30d)</p>
              </div>
            </div>
          )}

          {/* Charts */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 32 }}>
            {/* Quotes by Month */}
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: 24 }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Quotes by Month</h3>
              {analytics?.quotes_by_month && (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.quotes_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0D47A1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Quotes by Status Pie */}
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: 24 }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Quotes by Status</h3>
              {statusData.length > 0 && (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Quotes by Category */}
          <div style={{ background: 'white', border: '1px solid var(--border)', padding: 24 }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Quotes by Category</h3>
            {categoryData.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#475569' }} width={100} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {categoryData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
